'use client';

import { type RefObject, useEffect } from 'react';

import { signalSurveyActivity } from './shared-survey-idle-refresh.helpers';
import type { MobileUtSession } from './shared-survey-mobile-ut-phone-session.helpers';
import { ACTIVITY_SIGNAL_INTERVAL_MS } from './shared-survey-mobile-ut-phone.consts';
import type { MobileUtPhonePhase } from './shared-survey-mobile-ut-phone.types';

export function useMobileUtPhoneUnloadGuard(
  phase: MobileUtPhonePhase,
  sessionRef: RefObject<Pick<
    MobileUtSession,
    'handlePageHide' | 'resumeSuspendedAudio'
  > | null>
) {
  useEffect(() => {
    if (phase !== 'active') return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    const handlePageHide = (event: PageTransitionEvent) => {
      // Skip bfcache restore — page may revive without re-creating Hyperbeam.
      if (event.persisted) return;
      sessionRef.current?.handlePageHide();
    };

    // Mobile browsers can suspend AudioContext when tab is backgrounded; resume on return.
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      sessionRef.current?.resumeSuspendedAudio();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handlePageHide);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [phase, sessionRef]);
}

export function useMobileUtPhoneActivitySignal(phase: MobileUtPhonePhase) {
  useEffect(() => {
    if (phase !== 'active') return;

    const interval = setInterval(
      signalSurveyActivity,
      ACTIVITY_SIGNAL_INTERVAL_MS
    );
    return () => clearInterval(interval);
  }, [phase]);
}
